import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Flame, Heart, Users, Globe, BookOpen, HandHeart, Sparkles, Crown } from "lucide-react";
import api from "@/lib/api";

const VALUES = [
  { icon: Flame, title: "On Fire", desc: "We burn for Jesus and refuse to play small." },
  { icon: Heart, title: "Family First", desc: "Every soul matters. You belong here before you believe here." },
  { icon: Globe, title: "Seven Mountains", desc: "Called to dominate every sphere of society for God's glory." },
  { icon: Users, title: "Spirit-Filled", desc: "Pentecostal in conviction — empowered by the Holy Spirit for impact." },
];

const PILLARS = [
  { icon: BookOpen, title: "The Holy Bible", desc: "The inspired Word of God that guides our faith, shapes our beliefs, and directs our daily walk with Christ." },
  { icon: HandHeart, title: "The Fellowship", desc: "A family of believers united in love, growing together in faith and Christ." },
  { icon: Sparkles, title: "The Grace", desc: "The unmerited favour of God that saves, sustains, and strengthens us daily in Christ." },
  { icon: Flame, title: "The Anointing", desc: "The power of God upon our lives that enables service, breakthrough, and spiritual impact." },
  { icon: Crown, title: "The Covenant", desc: "God's unbreakable promise that secures our faith, future, and relationship with Him." },
];

export default function About() {
  const [c, setC] = useState({});
  const [milestone, setMilestone] = useState({});
  useEffect(() => {
    api.get("/content/about_page").then((r) => setC(r.data || {})).catch(() => {});
    api.get("/content/home_milestone").then((r) => setMilestone(r.data || {})).catch(() => {});
  }, []);
  return (
    <div data-testid="about-page" className="bg-[#050505] text-white">
      <section className="relative py-32 md:py-40 px-6 md:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">{c.eyebrow || "About UGYC"}</p>
          <h1 data-testid="about-heading" className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9] max-w-5xl">
            {c.headline_lead || "A Christ-centered"} <span className="text-[#ff5c00]">{c.headline_accent || "family"}</span> {c.headline_tail || "growing in faith, love, and God's Word."}
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            {c.intro_body || "UGYC is a parish of The Redeemed Christian Church of God, based in Dubai. We are a Christ-centered family committed to worship, prayer, and the teaching of God's Word — raising lives transformed through faith, love, and the power of the Holy Spirit."}
          </p>
        </div>
      </section>

      {(milestone.value || milestone.label) && (
        <section data-testid="about-milestone-section" className="py-16 md:py-20 px-6 md:px-12 border-b border-white/5">
          <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">{milestone.eyebrow || "By His Grace"}</p>
              <div className="flex items-baseline gap-4">
                <span data-testid="about-milestone-value" className="font-display text-7xl md:text-8xl lg:text-9xl text-[#ff5c00] leading-[0.8]">{milestone.value || "13+"}</span>
                <span data-testid="about-milestone-label" className="font-display uppercase tracking-tight text-2xl md:text-3xl text-white leading-tight">{milestone.label || "Years of Ministry"}</span>
              </div>
            </div>
            {milestone.body && (
              <p className="md:col-span-7 text-zinc-300 text-base md:text-lg leading-relaxed border-l border-[#ff5c00]/40 md:pl-8">
                {milestone.body}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="py-24 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Mission</p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-4">{c.mission_heading || "Raising lives transformed through Christ."}</h2>
            <p className="text-zinc-300 leading-relaxed">
              {c.mission_body || "To raise people who love God, grow in faith, and impact their world through the power of the Holy Spirit. We are a family where lives are transformed, destinies are shaped, and everyone is welcome to experience the love of Christ."}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Our Approach</p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-4">{c.approach_heading || "Rooted in prayer, worship, and the Word."}</h2>
            <p className="text-zinc-300 leading-relaxed">
              {c.approach_body || "A Spirit-filled Pentecostal church set to dominate the seven mountains of societal influence for God's glory — through prayer, anointed worship, the teaching of God's Word, and raising lives transformed by Jesus."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">What We Stand For</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-12">Our Values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`value-card-${i}`}
                className="bg-[#0f0f14] border border-white/10 p-7 rounded-sm hover:border-[#ff5c00]/60 hover:-translate-y-1 transition-all"
              >
                <v.icon className="w-7 h-7 text-[#ff5c00] mb-5" />
                <h3 className="font-display text-xl uppercase tracking-tight mb-2">{v.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">The Word We Stand On</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-12 max-w-4xl">
            Five pillars holding up our faith.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`pillar-${i}`}
                className="bg-[#0f0f14] border border-white/10 p-7 rounded-sm hover:border-[#ff5c00]/50 transition-colors"
              >
                <p.icon className="w-6 h-6 text-[#ff5c00] mb-4" />
                <h3 className="font-display text-xl uppercase tracking-tight mb-2">{p.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Leadership</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-6">Pastored with faith and wisdom.</h2>
          <div className="mt-10 bg-[#0f0f14] border border-white/10 rounded-sm p-10 inline-block">
            {c.pastor_image ? (
              <div className="w-24 h-24 mx-auto rounded-sm overflow-hidden mb-5">
                <img
                  src={c.pastor_image}
                  alt={c.pastor_name}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: c.pastor_image_position || "center",
                    transform: `scale(${c.pastor_image_scale || 1})`,
                    transformOrigin: c.pastor_image_position || "center",
                  }}
                />
              </div>
            ) : (
              <div className="w-16 h-16 mx-auto rounded-full bg-[#ff5c00]/15 flex items-center justify-center mb-5">
                <Flame className="w-7 h-7 text-[#ff5c00]" />
              </div>
            )}
            <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{c.pastor_title || "Lead Pastor"}</p>
            <h3 className="font-display text-3xl uppercase tracking-tight">{c.pastor_name || "Pastor Yemi Keazor"}</h3>
            <p className="text-zinc-400 max-w-md mt-4">
              {c.pastor_bio || "Shepherding the UGYC family with grace, vision and a passion to see every member walk in their God-given purpose."}
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#080808] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center mb-14">
            <div className="lg:col-span-3 flex justify-center lg:justify-start">
              <div className="bg-white/95 rounded-sm p-5 shadow-[0_10px_40px_-10px_rgba(255,92,0,0.25)]">
                <img
                  src="/rccg-logo.png"
                  alt="RCCG — The Redeemed Christian Church of God"
                  data-testid="rccg-logo-about"
                  className="w-32 h-32 object-contain"
                />
              </div>
            </div>
            <div className="lg:col-span-9 space-y-4 text-center lg:text-left">
              <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00]">Our Affiliation</p>
              <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-tight">
                A parish of <span className="text-[#ff5c00]">RCCG</span> worldwide.
              </h2>
              <p className="text-zinc-300 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                UGYC Dubai operates under The Redeemed Christian Church of God
                — a Pentecostal mission with parishes in over 190 nations.
                Our doctrine, governance and pastoral covering are aligned
                with the global RCCG family, walking together in one mission
                and vision.
              </p>
            </div>
          </div>

          <div className="bg-[#0f0f14] border border-white/10 rounded-sm p-8 md:p-12">
            <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-2">RCCG Worldwide</p>
            <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight mb-10">
              Mission & Vision
            </h3>
            <ol data-testid="rccg-mission-list" className="space-y-6">
              {[
                "To make heaven.",
                "To take as many people with us.",
                "To have a member of RCCG in every family of all nations.",
                "To accomplish No. 1 above, holiness will be our lifestyle.",
                "To accomplish No. 2 and 3 above, we will plant churches within five minutes walking distance in every city and town of developing countries, and within five minutes driving distance in every city and town of developed countries.",
                "We will pursue these objectives until every nation in the world is reached for the Lord Jesus Christ.",
              ].map((point, i) => (
                <li
                  key={i}
                  data-testid={`rccg-mission-${i}`}
                  className="flex items-start gap-5 group"
                >
                  <span className="font-display text-3xl md:text-4xl text-[#ff5c00] leading-none w-12 shrink-0 group-hover:scale-110 transition-transform">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-zinc-200 leading-relaxed pt-1 md:text-lg">
                    {point}
                  </p>
                </li>
              ))}
            </ol>

            <a
              href="https://www.rccg.org/mission-and-vision/"
              target="_blank"
              rel="noreferrer"
              data-testid="rccg-mission-link"
              className="inline-flex items-center gap-2 mt-10 border border-white/20 hover:border-[#ff5c00] hover:text-[#ff5c00] font-mono-accent text-xs tracking-[0.18em] uppercase px-6 py-3 rounded-sm transition-colors"
            >
              Read on RCCG.org
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
